/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import VideoPauseOverlayIcon from '@atlaskit/icon/core/video-pause-overlay';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';
import { useAudioPlayPauseShortcut } from '../../../hooks/useAudioPlayPauseShortcut';

const i18n = defineMessages({
	pauseButtonLabel: {
		id: 'audio.mini-pause-button.pause-button-label',
		defaultMessage: 'Pause',
		description:
			'Accessible label for the mini pause button in the audio player that pauses audio playback when clicked.',
	},
});

const styles = cssMap({
	pauseButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.neutral'),
	},
	pauseButtonHover: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.pressed'),
		},
	},
	pauseIcon: {
		display: 'inline-block',
		transform: 'scale(0.875)',
	},
});

export type MiniPauseButtonProps = {
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
};

export const MiniPauseButton = ({ onMouseEnter, onMouseLeave }: MiniPauseButtonProps) => {
	const { formatMessage } = useIntl();
	const { audioRef } = useAudioPlayerStreamingContext();
	const { trackAIResultAction } = useAIEventsInstrumentation();

	const handleClick = useCallback(() => {
		trackAIResultAction('audioPause', {
			attributes: {
				source: 'minified-audio-player',
			},
		});
		audioRef.current?.pause();
	}, [audioRef, trackAIResultAction]);

	useAudioPlayPauseShortcut(handleClick);

	return (
		<Tooltip content={formatMessage(i18n.pauseButtonLabel)}>
			<Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
				<Pressable
					onClick={handleClick}
					xcss={cx(styles.pauseButton, styles.pauseButtonHover)}
					testId="miniplayer-mini-pause-button"
				>
					<span css={styles.pauseIcon}>
						<VideoPauseOverlayIcon label={formatMessage(i18n.pauseButtonLabel)} />
					</span>
				</Pressable>
			</Box>
		</Tooltip>
	);
};
